import { QueryClient } from "@tanstack/react-query";

import { Export } from "~/api/Api";
import { queryKeys } from "~/api/queryKeys";
import { Authorizations } from "~/api/types";

export type ExportsQueryData = {
  exports: Export[];
  authorizations?: Authorizations;
};

const PENDING_STATUSES = new Set<Export["status"]>(["created", "processing"]);

const isOlder = (a: Export, b: Export) =>
  new Date(a.updated_at).getTime() < new Date(b.updated_at).getTime();

// Puts an export from a create response or an `export-status` message into
// the cached list of its type, so the table updates without a refetch.
export const upsertCachedExport = (
  queryClient: QueryClient,
  siteId: number | string,
  item: Export,
) => {
  const queryKey = queryKeys.exports(siteId, item.type);
  const query = queryClient.getQueryCache().find<ExportsQueryData>({
    queryKey,
    exact: true,
  });

  // Not cached yet: the list is fetched when its page opens.
  if (!query) return;

  // A list request in flight may have been answered before this change and
  // would bring the old status back, so start a new one instead.
  if (!query.state.data || query.state.fetchStatus === "fetching") {
    void queryClient.invalidateQueries({ queryKey, exact: true });

    return;
  }

  queryClient.setQueryData<ExportsQueryData>(queryKey, (prev) => {
    if (!prev) return prev;

    const current = prev.exports.find(({ id }) => id === item.id);

    // Messages aren't guaranteed to arrive in order.
    if (current && isOlder(item, current)) return prev;

    return {
      ...prev,
      exports: current
        ? prev.exports.map((exp) => (exp.id === item.id ? item : exp))
        : [item, ...prev.exports],
    };
  });
};

// Status messages sent while the channel was down are lost, so refetch the
// lists that still wait for an export to finish.
export const refetchPendingExports = (
  queryClient: QueryClient,
  siteId: number | string,
) =>
  queryClient.invalidateQueries({
    queryKey: queryKeys.exportsRoot(siteId),
    predicate: (query) =>
      Boolean(
        (query.state.data as ExportsQueryData | undefined)?.exports.some(
          ({ status }) => PENDING_STATUSES.has(status),
        ),
      ),
  });
